import React, {FC, PropsWithChildren, ReactNode} from 'react';

import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildContactAvatarUrl, buildContactInitials} from '../../../helpers/models/contactHelpers';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {Avatar, AvatarProps} from './avatar';
import {AvatarBackground} from './avatarBackground';
import {AvatarImage} from './avatarImage';
import {AvatarInitials} from './avatarInitials';

/*
 * Props.
 */

interface ContactAvatarProps extends AvatarProps {
  /** The contact to display. */
  visualContact: VisualContactModel;
  children?: ReactNode;
  className?: string;
  isChatAvatar?: boolean;
  isChatUserOnline?: boolean;
}

/*
 * Component.
 */

/** Display the avatar of the specified recipient. */
export const ContactAvatar: FC<PropsWithChildren<ContactAvatarProps>> = (props) => {
  // Check if the provided recipient has a custom avatar.

  const {visualContact} = props;
  const avatarUrl = buildContactAvatarUrl(visualContact);
  if (avatarUrl) {
    return renderContactImageAvatar(props, avatarUrl);
  }

  // Otherwise, render the default avatar.
  const initials = buildContactInitials(visualContact);
  return renderContactColorAvatar(props, initials);
};

function renderContactImageAvatar(props: ContactAvatarProps, recipientUrl: string) {
  return (
    <Avatar {...props}>
      <AvatarImage imageUrl={recipientUrl} />
      {props.children}
    </Avatar>
  );
}

function renderContactColorAvatar(props: ContactAvatarProps, initials: string) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Avatar {...props}>
          <AvatarBackground color={theme.greys.shade70} />
          <AvatarInitials size={props.size} textColor={theme.invertedText} value={initials} />
          {props.children}
        </Avatar>
      )}
    </UserThemeConsumer>
  );
}
